import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import ConfirmModal from "../../common/ConfirmModal";
import { useLoader } from "../../../context/LoaderContext";
import { useUserData } from "../../../hooks/useUserData";
import {
  parsePermissions,
  hasPermission,
  getAllPermissionKeys,
  isSystemAdministratorRole,
  getSessionRoleName,
} from "../../../utils/permissionUtils";
import {
  fetchCapaConfig,
  saveCapaConfig,
  fetchCapaList,
} from "../capa/capaService";
import CapaActionPlanningEmailConfigEditor from "../capa/CapaActionPlanningEmailConfigEditor";
import { CAPA_ACTION_PLANNING_DECISION_EMAIL_MERGE_TAGS } from "../capa/capaActionPlanningEmailTags";
import {
  CAPA_APPROVAL_ASSIGNMENT_EMAIL_MERGE_TAGS,
  CAPA_APPROVAL_DECISION_EMAIL_MERGE_TAGS,
} from "../capa/capaApprovalEmailTags";
import {
  CAPA_IMPLEMENTATION_ASSIGNMENT_EMAIL_MERGE_TAGS,
  CAPA_IMPLEMENTATION_OWNER_DECISION_EMAIL_MERGE_TAGS,
} from "../capa/capaImplementationEmailTags";
import {
  CAPA_VERIFICATION_ASSIGNMENT_EMAIL_MERGE_TAGS,
  CAPA_VERIFICATION_OWNER_OUTCOME_EMAIL_MERGE_TAGS,
} from "../capa/capaVerificationEmailTags";
import { expandCapaStatusUsageKeys, expandCapaRcaMethodUsageKeys } from "../capa/capaWorkflow";
import { CAPA_RCA_METHOD_OPTIONS } from "../capa/capaConstants";
import "./CapaConfigMaster.scss";

const CONFIG_SECTIONS = [
  {
    key: "numbering",
    label: "Numbering & SLA",
    subtitle: "ID format and due timeline defaults",
  },
  {
    key: "sourceTypes",
    label: "Source Types",
    subtitle: "CAPA source dropdown values",
  },
  {
    key: "rcaMethods",
    label: "RCA Methods",
    subtitle: "Root cause analysis method values",
  },
  {
    key: "statuses",
    label: "CAPA Statuses",
    subtitle: "Workflow status master values",
  },
  {
    key: "actionPlanningEmail",
    label: "Action planning email",
    subtitle: "Task assignment notification templates",
  },
  {
    key: "actionPlanningTaskAcceptEmail",
    label: "Action planning — task accepted",
    subtitle: "Notify CAPA owner when assignee accepts",
  },
  {
    key: "actionPlanningTaskRejectEmail",
    label: "Action planning — task rejected",
    subtitle: "Notify CAPA owner when assignee rejects",
  },
  {
    key: "approvalAssignmentEmail",
    label: "Approval — assignment",
    subtitle: "Notify staff when assigned as approver",
  },
  {
    key: "approvalDecisionAcceptEmail",
    label: "Approval — accepted",
    subtitle: "Notify CAPA owner when approver accepts",
  },
  {
    key: "approvalDecisionRejectEmail",
    label: "Approval — rejected",
    subtitle: "Notify CAPA owner when approver rejects",
  },
  {
    key: "implementationAssignmentEmail",
    label: "Implementation — task assignment",
    subtitle: "Notify staff when assigned an implementation task",
  },
  {
    key: "implementationStatusChangeEmail",
    label: "Implementation — status change",
    subtitle: "Notify CAPA owner when implementer updates status",
  },
  {
    key: "verificationAssignmentEmail",
    label: "Verification — assignment",
    subtitle: "Notify staff when assigned a verification request",
  },
  {
    key: "verificationOutcomeEffectiveEmail",
    label: "Verification — effective",
    subtitle: "Notify CAPA owner when verifier marks outcome Effective",
  },
  {
    key: "verificationOutcomeNotEffectiveEmail",
    label: "Verification — not effective",
    subtitle: "Notify CAPA owner when verifier marks outcome Not Effective",
  },
];

const DEFAULT_RCA_METHODS = [
  "5 Whys",
  "Fishbone",
  "Pareto Analysis",
  "Fault Tree Analysis",
  "Other",
];

function normalizeTextList(input) {
  const arr = Array.isArray(input) ? input : [];
  return arr
    .map((v) => String(v || "").trim())
    .filter(Boolean)
    .filter((v, i, a) => a.findIndex((x) => x.toLowerCase() === v.toLowerCase()) === i);
}

function toConfigSnapshot({
  numberingFormat,
  defaultSlaDays,
  statuses,
  sourceTypes,
  rcaMethods,
  actionPlanningEmailSubjectTemplate,
  actionPlanningEmailBodyTemplate,
  actionPlanningTaskAcceptEmailSubjectTemplate,
  actionPlanningTaskAcceptEmailBodyTemplate,
  actionPlanningTaskRejectEmailSubjectTemplate,
  actionPlanningTaskRejectEmailBodyTemplate,
  approvalAssignmentEmailSubjectTemplate,
  approvalAssignmentEmailBodyTemplate,
  approvalDecisionAcceptEmailSubjectTemplate,
  approvalDecisionAcceptEmailBodyTemplate,
  approvalDecisionRejectEmailSubjectTemplate,
  approvalDecisionRejectEmailBodyTemplate,
  implAssignmentEmailSubjectTemplate,
  implAssignmentEmailBodyTemplate,
  implOwnerAcceptEmailSubjectTemplate,
  implOwnerAcceptEmailBodyTemplate,
  verificationAssignmentEmailSubjectTemplate,
  verificationAssignmentEmailBodyTemplate,
  verificationOutcomeEffectiveEmailSubjectTemplate,
  verificationOutcomeEffectiveEmailBodyTemplate,
  verificationOutcomeNotEffectiveEmailSubjectTemplate,
  verificationOutcomeNotEffectiveEmailBodyTemplate,
}) {
  return JSON.stringify({
    numberingFormat: String(numberingFormat || "").trim(),
    defaultSlaDays: Number(defaultSlaDays || 0),
    statuses: normalizeTextList(statuses),
    sourceTypes: normalizeTextList(sourceTypes),
    rcaMethods: normalizeTextList(rcaMethods),
    actionPlanningEmailSubjectTemplate: String(actionPlanningEmailSubjectTemplate || "").trim(),
    actionPlanningEmailBodyTemplate: String(actionPlanningEmailBodyTemplate || "").trim(),
    actionPlanningTaskAcceptEmailSubjectTemplate: String(
      actionPlanningTaskAcceptEmailSubjectTemplate || "",
    ).trim(),
    actionPlanningTaskAcceptEmailBodyTemplate: String(
      actionPlanningTaskAcceptEmailBodyTemplate || "",
    ).trim(),
    actionPlanningTaskRejectEmailSubjectTemplate: String(
      actionPlanningTaskRejectEmailSubjectTemplate || "",
    ).trim(),
    actionPlanningTaskRejectEmailBodyTemplate: String(
      actionPlanningTaskRejectEmailBodyTemplate || "",
    ).trim(),
    approvalAssignmentEmailSubjectTemplate: String(approvalAssignmentEmailSubjectTemplate || "").trim(),
    approvalAssignmentEmailBodyTemplate: String(approvalAssignmentEmailBodyTemplate || "").trim(),
    approvalDecisionAcceptEmailSubjectTemplate: String(
      approvalDecisionAcceptEmailSubjectTemplate || "",
    ).trim(),
    approvalDecisionAcceptEmailBodyTemplate: String(
      approvalDecisionAcceptEmailBodyTemplate || "",
    ).trim(),
    approvalDecisionRejectEmailSubjectTemplate: String(
      approvalDecisionRejectEmailSubjectTemplate || "",
    ).trim(),
    approvalDecisionRejectEmailBodyTemplate: String(
      approvalDecisionRejectEmailBodyTemplate || "",
    ).trim(),
    implAssignmentEmailSubjectTemplate: String(implAssignmentEmailSubjectTemplate || "").trim(),
    implAssignmentEmailBodyTemplate: String(implAssignmentEmailBodyTemplate || "").trim(),
    implOwnerAcceptEmailSubjectTemplate: String(implOwnerAcceptEmailSubjectTemplate || "").trim(),
    implOwnerAcceptEmailBodyTemplate: String(implOwnerAcceptEmailBodyTemplate || "").trim(),
    verificationAssignmentEmailSubjectTemplate: String(
      verificationAssignmentEmailSubjectTemplate || "",
    ).trim(),
    verificationAssignmentEmailBodyTemplate: String(
      verificationAssignmentEmailBodyTemplate || "",
    ).trim(),
    verificationOutcomeEffectiveEmailSubjectTemplate: String(
      verificationOutcomeEffectiveEmailSubjectTemplate || "",
    ).trim(),
    verificationOutcomeEffectiveEmailBodyTemplate: String(
      verificationOutcomeEffectiveEmailBodyTemplate || "",
    ).trim(),
    verificationOutcomeNotEffectiveEmailSubjectTemplate: String(
      verificationOutcomeNotEffectiveEmailSubjectTemplate || "",
    ).trim(),
    verificationOutcomeNotEffectiveEmailBodyTemplate: String(
      verificationOutcomeNotEffectiveEmailBodyTemplate || "",
    ).trim(),
  });
}

function MasterListEditor({
  title,
  description,
  items,
  setItems,
  usedValues,
  enforceUsageCheck = true,
  readOnly,
  onSave,
  saving,
  reloading,
  saveLabel,
  onNotify,
  showUnsavedNotice,
}) {
  const [newValue, setNewValue] = useState("");
  const [editIndex, setEditIndex] = useState(-1);
  const [editValue, setEditValue] = useState("");

  const addItem = () => {
    const value = String(newValue || "").trim();
    if (!value) {
      onNotify?.({
        title: title,
        message: "Please enter a value before adding.",
        type: "warning",
      });
      return;
    }
    if (items.some((v) => v.toLowerCase() === value.toLowerCase())) {
      onNotify?.({
        title: title,
        message: `"${value}" already exists.`,
        type: "warning",
      });
      return;
    }
    setItems([...items, value].sort((a, b) => a.localeCompare(b)));
    setNewValue("");
    onNotify?.({
      title: title,
      message: `"${value}" added successfully.`,
      type: "success",
    });
  };

  const startRename = (idx) => {
    setEditIndex(idx);
    setEditValue(items[idx] || "");
  };

  const saveRename = () => {
    if (editIndex < 0) return;
    const oldVal = items[editIndex];
    const nextVal = String(editValue || "").trim();
    if (!nextVal) {
      onNotify?.({
        title: title,
        message: "Updated value cannot be empty.",
        type: "warning",
      });
      return;
    }
    if (
      enforceUsageCheck &&
      oldVal.toLowerCase() !== nextVal.toLowerCase() &&
      usedValues.has(oldVal.toLowerCase())
    ) {
      onNotify?.({
        title: title,
        message: `"${oldVal}" is already in use and cannot be renamed.`,
        type: "warning",
      });
      return;
    }
    if (
      items.some(
        (v, i) => i !== editIndex && v.toLowerCase() === nextVal.toLowerCase(),
      )
    ) {
      onNotify?.({
        title: title,
        message: `"${nextVal}" already exists.`,
        type: "warning",
      });
      return;
    }
    const next = [...items];
    next[editIndex] = nextVal;
    setItems(next.sort((a, b) => a.localeCompare(b)));
    setEditIndex(-1);
    setEditValue("");
    onNotify?.({
      title: title,
      message: `"${oldVal}" updated to "${nextVal}".`,
      type: "success",
    });
  };

  const deleteItem = (idx) => {
    const value = items[idx];
    if (enforceUsageCheck && usedValues.has(String(value).toLowerCase())) {
      onNotify?.({
        title: title,
        message: `"${value}" is in use and cannot be deleted.`,
        type: "warning",
      });
      return;
    }
    setItems(items.filter((_, i) => i !== idx));
    onNotify?.({
      title: title,
      message: `"${value}" removed successfully.`,
      type: "success",
    });
  };

  return (
    <div className="card shadow-sm capa-config-saas__panel-card">
      <div className="card-body">
        {showUnsavedNotice ? (
          <div className="alert alert-warning py-2 px-3 mb-3">
            <i className="fas fa-exclamation-triangle me-2" />
            You have unsaved changes. Click section <strong>Save</strong> to apply updates.
          </div>
        ) : null}
        <h6 className="mb-1 fw-semibold">{title}</h6>
        <p className="text-muted small mb-3">{description}</p>

        {!readOnly && (
          <div className="input-group mb-3">
            <input
              className="form-control"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder={`Add new ${title.toLowerCase().slice(0, -1)}`}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addItem();
                }
              }}
            />
            <button type="button" className="btn btn-primary" onClick={addItem}>
              Add
            </button>
          </div>
        )}

        <ul className="list-group shadow-sm">
          {items.length === 0 ? (
            <li className="list-group-item text-muted small">
              No values configured yet.
            </li>
          ) : null}
          {items.map((item, idx) => {
            const inUse = enforceUsageCheck && usedValues.has(String(item).toLowerCase());
            const isEditing = editIndex === idx;
            return (
              <li
                key={`${item}-${idx}`}
                className="list-group-item d-flex align-items-center justify-content-between gap-2"
              >
                <div className="flex-grow-1">
                  {isEditing ? (
                    <input
                      className="form-control form-control-sm"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                    />
                  ) : (
                    <span>{item}</span>
                  )}
                  {inUse ? <div className="small text-warning mt-1">In use by CAPA</div> : null}
                </div>

                {!readOnly && !inUse && (
                  <div className="d-flex gap-2">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-sm btn-success"
                          onClick={saveRename}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => {
                            setEditIndex(-1);
                            setEditValue("");
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => startRename(idx)}
                          disabled={inUse}
                          title={inUse ? "Cannot rename while in use" : "Rename"}
                        >
                          Rename
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => deleteItem(idx)}
                          disabled={inUse}
                          title={inUse ? "Cannot delete while in use" : "Delete"}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        {!readOnly ? (
          <div className="d-flex justify-content-end mt-3">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={onSave}
              disabled={saving || reloading}
            >
              {saving || reloading ? (saving ? "Saving..." : "Loading...") : saveLabel || "Save"}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const CapaConfigMaster = () => {
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();

  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("numbering");

  const [numberingFormat, setNumberingFormat] = useState("CAPA-{YYYY}-{SEQ}");
  const [defaultSlaDays, setDefaultSlaDays] = useState(30);
  const [statuses, setStatuses] = useState([]);
  const [sourceTypes, setSourceTypes] = useState([]);
  const [rcaMethods, setRcaMethods] = useState(DEFAULT_RCA_METHODS);
  const [actionPlanningEmailSubjectTemplate, setActionPlanningEmailSubjectTemplate] =
    useState("");
  const [actionPlanningEmailBodyTemplate, setActionPlanningEmailBodyTemplate] = useState("");
  const [
    actionPlanningTaskAcceptEmailSubjectTemplate,
    setActionPlanningTaskAcceptEmailSubjectTemplate,
  ] = useState("");
  const [actionPlanningTaskAcceptEmailBodyTemplate, setActionPlanningTaskAcceptEmailBodyTemplate] =
    useState("");
  const [
    actionPlanningTaskRejectEmailSubjectTemplate,
    setActionPlanningTaskRejectEmailSubjectTemplate,
  ] = useState("");
  const [actionPlanningTaskRejectEmailBodyTemplate, setActionPlanningTaskRejectEmailBodyTemplate] =
    useState("");
  const [approvalAssignmentEmailSubjectTemplate, setApprovalAssignmentEmailSubjectTemplate] =
    useState("");
  const [approvalAssignmentEmailBodyTemplate, setApprovalAssignmentEmailBodyTemplate] =
    useState("");
  const [approvalDecisionAcceptEmailSubjectTemplate, setApprovalDecisionAcceptEmailSubjectTemplate] =
    useState("");
  const [approvalDecisionAcceptEmailBodyTemplate, setApprovalDecisionAcceptEmailBodyTemplate] =
    useState("");
  const [approvalDecisionRejectEmailSubjectTemplate, setApprovalDecisionRejectEmailSubjectTemplate] =
    useState("");
  const [approvalDecisionRejectEmailBodyTemplate, setApprovalDecisionRejectEmailBodyTemplate] =
    useState("");
  const [implAssignmentEmailSubjectTemplate, setImplAssignmentEmailSubjectTemplate] = useState("");
  const [implAssignmentEmailBodyTemplate, setImplAssignmentEmailBodyTemplate] = useState("");
  const [
    implOwnerAcceptEmailSubjectTemplate,
    setImplOwnerAcceptEmailSubjectTemplate,
  ] = useState("");
  const [implOwnerAcceptEmailBodyTemplate, setImplOwnerAcceptEmailBodyTemplate] = useState("");
  const [
    verificationAssignmentEmailSubjectTemplate,
    setVerificationAssignmentEmailSubjectTemplate,
  ] = useState("");
  const [verificationAssignmentEmailBodyTemplate, setVerificationAssignmentEmailBodyTemplate] =
    useState("");
  const [
    verificationOutcomeEffectiveEmailSubjectTemplate,
    setVerificationOutcomeEffectiveEmailSubjectTemplate,
  ] = useState("");
  const [
    verificationOutcomeEffectiveEmailBodyTemplate,
    setVerificationOutcomeEffectiveEmailBodyTemplate,
  ] = useState("");
  const [
    verificationOutcomeNotEffectiveEmailSubjectTemplate,
    setVerificationOutcomeNotEffectiveEmailSubjectTemplate,
  ] = useState("");
  const [
    verificationOutcomeNotEffectiveEmailBodyTemplate,
    setVerificationOutcomeNotEffectiveEmailBodyTemplate,
  ] = useState("");
  const [capaRows, setCapaRows] = useState([]);
  const [savedSnapshot, setSavedSnapshot] = useState("");
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    confirmButtonClass: "btn-primary",
    icon: "fas fa-question-circle text-warning",
    onConfirm: null,
  });

  const isSystemAdmin = isSystemAdministratorRole(getSessionRoleName(userData));
  const userPermissions = isSystemAdmin
    ? getAllPermissionKeys()
    : parsePermissions(userData?.Permission || "");
  const canConfigureCapa =
    isSystemAdmin || hasPermission(userPermissions, "CAPA_CONFIGURE");

  const statusUsage = useMemo(() => {
    const used = new Set();
    capaRows.forEach((row) => {
      expandCapaStatusUsageKeys(row?.Status).forEach((key) => used.add(key));
    });
    return used;
  }, [capaRows]);
  const sourceTypeUsage = useMemo(
    () =>
      new Set(
        capaRows
          .map((r) => String(r?.SourceType || "").trim().toLowerCase())
          .filter(Boolean),
      ),
    [capaRows],
  );
  const rcaMethodUsage = useMemo(() => {
    const used = new Set();
    capaRows.forEach((row) => {
      expandCapaRcaMethodUsageKeys(
        row?.RcaMethod || row?.rcaMethod,
        CAPA_RCA_METHOD_OPTIONS,
      ).forEach((key) => used.add(key));
    });
    return used;
  }, [capaRows]);

  const numberingTokenWarnings = useMemo(() => {
    const format = String(numberingFormat || "");
    const warnings = [];
    if (!format.includes("{SEQ}")) {
      warnings.push("`{SEQ}` token missing: unique running number should be included.");
    }
    if (!format.includes("CAPA")) {
      warnings.push("Prefix `CAPA` is recommended for easy identification.");
    }
    if (!format.includes("{YYYY}") && !format.includes("{YY}")) {
      warnings.push("Year token (`{YYYY}` or `{YY}`) is recommended.");
    }
    return warnings;
  }, [numberingFormat]);

  const currentSnapshot = useMemo(
    () =>
      toConfigSnapshot({
        numberingFormat,
        defaultSlaDays,
        statuses,
        sourceTypes,
        rcaMethods,
        actionPlanningEmailSubjectTemplate,
        actionPlanningEmailBodyTemplate,
        actionPlanningTaskAcceptEmailSubjectTemplate,
        actionPlanningTaskAcceptEmailBodyTemplate,
        actionPlanningTaskRejectEmailSubjectTemplate,
        actionPlanningTaskRejectEmailBodyTemplate,
        approvalAssignmentEmailSubjectTemplate,
        approvalAssignmentEmailBodyTemplate,
        approvalDecisionAcceptEmailSubjectTemplate,
        approvalDecisionAcceptEmailBodyTemplate,
        approvalDecisionRejectEmailSubjectTemplate,
        approvalDecisionRejectEmailBodyTemplate,
        implAssignmentEmailSubjectTemplate,
        implAssignmentEmailBodyTemplate,
        implOwnerAcceptEmailSubjectTemplate,
        implOwnerAcceptEmailBodyTemplate,
        verificationAssignmentEmailSubjectTemplate,
        verificationAssignmentEmailBodyTemplate,
        verificationOutcomeEffectiveEmailSubjectTemplate,
        verificationOutcomeEffectiveEmailBodyTemplate,
        verificationOutcomeNotEffectiveEmailSubjectTemplate,
        verificationOutcomeNotEffectiveEmailBodyTemplate,
      }),
    [
      numberingFormat,
      defaultSlaDays,
      statuses,
      sourceTypes,
      rcaMethods,
      actionPlanningEmailSubjectTemplate,
      actionPlanningEmailBodyTemplate,
      actionPlanningTaskAcceptEmailSubjectTemplate,
      actionPlanningTaskAcceptEmailBodyTemplate,
      actionPlanningTaskRejectEmailSubjectTemplate,
      actionPlanningTaskRejectEmailBodyTemplate,
      approvalAssignmentEmailSubjectTemplate,
      approvalAssignmentEmailBodyTemplate,
      approvalDecisionAcceptEmailSubjectTemplate,
      approvalDecisionAcceptEmailBodyTemplate,
      approvalDecisionRejectEmailSubjectTemplate,
      approvalDecisionRejectEmailBodyTemplate,
      implAssignmentEmailSubjectTemplate,
      implAssignmentEmailBodyTemplate,
      implOwnerAcceptEmailSubjectTemplate,
      implOwnerAcceptEmailBodyTemplate,
      verificationAssignmentEmailSubjectTemplate,
      verificationAssignmentEmailBodyTemplate,
      verificationOutcomeEffectiveEmailSubjectTemplate,
      verificationOutcomeEffectiveEmailBodyTemplate,
      verificationOutcomeNotEffectiveEmailSubjectTemplate,
      verificationOutcomeNotEffectiveEmailBodyTemplate,
    ],
  );

  const hasUnsavedChanges = useMemo(
    () => Boolean(savedSnapshot) && currentSnapshot !== savedSnapshot,
    [currentSnapshot, savedSnapshot],
  );

  const loadConfig = async ({ showBlockingLoader = false, useGlobalLoader = true } = {}) => {
    if (!userData?.OrganizationId) return;
    if (showBlockingLoader) {
      setLoading(true);
    } else {
      setReloading(true);
    }
    if (useGlobalLoader) {
      showLoader("Loading CAPA configuration...");
    }
    try {
      const cfg = await fetchCapaConfig(userData);
      let rows = [];
      try {
        rows = await fetchCapaList(userData);
      } catch (_listErr) {
        rows = [];
      }
      setCapaRows(Array.isArray(rows) ? rows : []);
      setNumberingFormat(cfg?.numberingFormat || "CAPA-{YYYY}-{SEQ}");
      setDefaultSlaDays(Number(cfg?.defaultSlaDays || 30));
      setStatuses(normalizeTextList(cfg?.statuses || []));
      setSourceTypes(normalizeTextList(cfg?.sourceTypes || []));
      setRcaMethods(normalizeTextList(cfg?.rcaMethods || DEFAULT_RCA_METHODS));
      const subj = String(cfg?.actionPlanningEmailSubjectTemplate || "").trim();
      const bodyTpl = String(cfg?.actionPlanningEmailBodyTemplate || "").trim();
      setActionPlanningEmailSubjectTemplate(subj);
      setActionPlanningEmailBodyTemplate(bodyTpl);
      const accSubj = String(cfg?.actionPlanningTaskAcceptEmailSubjectTemplate || "").trim();
      const accBody = String(cfg?.actionPlanningTaskAcceptEmailBodyTemplate || "").trim();
      const rejSubj = String(cfg?.actionPlanningTaskRejectEmailSubjectTemplate || "").trim();
      const rejBody = String(cfg?.actionPlanningTaskRejectEmailBodyTemplate || "").trim();
      setActionPlanningTaskAcceptEmailSubjectTemplate(accSubj);
      setActionPlanningTaskAcceptEmailBodyTemplate(accBody);
      setActionPlanningTaskRejectEmailSubjectTemplate(rejSubj);
      setActionPlanningTaskRejectEmailBodyTemplate(rejBody);
      const apvAsSubj = String(cfg?.approvalAssignmentEmailSubjectTemplate || "").trim();
      const apvAsBody = String(cfg?.approvalAssignmentEmailBodyTemplate || "").trim();
      const apvAccSubj = String(cfg?.approvalDecisionAcceptEmailSubjectTemplate || "").trim();
      const apvAccBody = String(cfg?.approvalDecisionAcceptEmailBodyTemplate || "").trim();
      const apvRejSubj = String(cfg?.approvalDecisionRejectEmailSubjectTemplate || "").trim();
      const apvRejBody = String(cfg?.approvalDecisionRejectEmailBodyTemplate || "").trim();
      setApprovalAssignmentEmailSubjectTemplate(apvAsSubj);
      setApprovalAssignmentEmailBodyTemplate(apvAsBody);
      setApprovalDecisionAcceptEmailSubjectTemplate(apvAccSubj);
      setApprovalDecisionAcceptEmailBodyTemplate(apvAccBody);
      setApprovalDecisionRejectEmailSubjectTemplate(apvRejSubj);
      setApprovalDecisionRejectEmailBodyTemplate(apvRejBody);
      const implAsgS = String(cfg?.implAssignmentEmailSubjectTemplate || "").trim();
      const implAsgB = String(cfg?.implAssignmentEmailBodyTemplate || "").trim();
      const implOaS = String(cfg?.implOwnerAcceptEmailSubjectTemplate || "").trim();
      const implOaB = String(cfg?.implOwnerAcceptEmailBodyTemplate || "").trim();
      setImplAssignmentEmailSubjectTemplate(implAsgS);
      setImplAssignmentEmailBodyTemplate(implAsgB);
      setImplOwnerAcceptEmailSubjectTemplate(implOaS);
      setImplOwnerAcceptEmailBodyTemplate(implOaB);
      const verAsgS = String(cfg?.verificationAssignmentEmailSubjectTemplate || "").trim();
      const verAsgB = String(cfg?.verificationAssignmentEmailBodyTemplate || "").trim();
      const verEffS = String(cfg?.verificationOutcomeEffectiveEmailSubjectTemplate || "").trim();
      const verEffB = String(cfg?.verificationOutcomeEffectiveEmailBodyTemplate || "").trim();
      const verNeS = String(cfg?.verificationOutcomeNotEffectiveEmailSubjectTemplate || "").trim();
      const verNeB = String(cfg?.verificationOutcomeNotEffectiveEmailBodyTemplate || "").trim();
      setVerificationAssignmentEmailSubjectTemplate(verAsgS);
      setVerificationAssignmentEmailBodyTemplate(verAsgB);
      setVerificationOutcomeEffectiveEmailSubjectTemplate(verEffS);
      setVerificationOutcomeEffectiveEmailBodyTemplate(verEffB);
      setVerificationOutcomeNotEffectiveEmailSubjectTemplate(verNeS);
      setVerificationOutcomeNotEffectiveEmailBodyTemplate(verNeB);
      setSavedSnapshot(
        toConfigSnapshot({
          numberingFormat: cfg?.numberingFormat || "CAPA-{YYYY}-{SEQ}",
          defaultSlaDays: Number(cfg?.defaultSlaDays || 30),
          statuses: normalizeTextList(cfg?.statuses || []),
          sourceTypes: normalizeTextList(cfg?.sourceTypes || []),
          rcaMethods: normalizeTextList(cfg?.rcaMethods || DEFAULT_RCA_METHODS),
          actionPlanningEmailSubjectTemplate: subj,
          actionPlanningEmailBodyTemplate: bodyTpl,
          actionPlanningTaskAcceptEmailSubjectTemplate: accSubj,
          actionPlanningTaskAcceptEmailBodyTemplate: accBody,
          actionPlanningTaskRejectEmailSubjectTemplate: rejSubj,
          actionPlanningTaskRejectEmailBodyTemplate: rejBody,
          approvalAssignmentEmailSubjectTemplate: apvAsSubj,
          approvalAssignmentEmailBodyTemplate: apvAsBody,
          approvalDecisionAcceptEmailSubjectTemplate: apvAccSubj,
          approvalDecisionAcceptEmailBodyTemplate: apvAccBody,
          approvalDecisionRejectEmailSubjectTemplate: apvRejSubj,
          approvalDecisionRejectEmailBodyTemplate: apvRejBody,
          implAssignmentEmailSubjectTemplate: implAsgS,
          implAssignmentEmailBodyTemplate: implAsgB,
          implOwnerAcceptEmailSubjectTemplate: implOaS,
          implOwnerAcceptEmailBodyTemplate: implOaB,
          verificationAssignmentEmailSubjectTemplate: verAsgS,
          verificationAssignmentEmailBodyTemplate: verAsgB,
          verificationOutcomeEffectiveEmailSubjectTemplate: verEffS,
          verificationOutcomeEffectiveEmailBodyTemplate: verEffB,
          verificationOutcomeNotEffectiveEmailSubjectTemplate: verNeS,
          verificationOutcomeNotEffectiveEmailBodyTemplate: verNeB,
        }),
      );
    } catch (_e) {
      showAlert({
        title: "CAPA Config",
        message: "Unable to load CAPA config.",
        type: "danger",
      });
    } finally {
      if (useGlobalLoader) {
        hideLoader();
      }
      if (showBlockingLoader) {
        setLoading(false);
      } else {
        setReloading(false);
      }
    }
  };

  useEffect(() => {
    loadConfig({ showBlockingLoader: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.OrganizationId, userData?.StaffId]);

  const showConfirmModal = (config) => {
    setConfirmState({
      isOpen: true,
      title: config.title || "Confirm Action",
      message: config.message || "Are you sure?",
      confirmText: config.confirmText || "Confirm",
      cancelText: config.cancelText || "Cancel",
      confirmButtonClass: config.confirmButtonClass || "btn-primary",
      icon: config.icon || "fas fa-question-circle text-warning",
      onConfirm: config.onConfirm || null,
    });
  };

  const closeConfirmModal = () => {
    setConfirmState((prev) => ({ ...prev, isOpen: false, onConfirm: null }));
  };

  const executeSave = async (scopeLabel = "CAPA configuration") => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;
    const sla = Number(defaultSlaDays);
    if (!Number.isFinite(sla) || sla <= 0) {
      showAlert({
        title: "Validation",
        message: "Default SLA must be greater than 0.",
        type: "warning",
      });
      return;
    }
    if (!statuses.length || !sourceTypes.length || !rcaMethods.length) {
      showAlert({
        title: "Validation",
        message: "Statuses, Source Types, and RCA Methods must have at least one value.",
        type: "warning",
      });
      return;
    }

    showLoader(`Saving ${scopeLabel}...`);
    setSaving(true);
    try {
      await saveCapaConfig(
        {
          numberingFormat,
          defaultSlaDays: sla,
          statuses: normalizeTextList(statuses),
          sourceTypes: normalizeTextList(sourceTypes),
          rcaMethods: normalizeTextList(rcaMethods),
          actionPlanningEmailSubjectTemplate,
          actionPlanningEmailBodyTemplate,
          actionPlanningTaskAcceptEmailSubjectTemplate,
          actionPlanningTaskAcceptEmailBodyTemplate,
          actionPlanningTaskRejectEmailSubjectTemplate,
          actionPlanningTaskRejectEmailBodyTemplate,
          approvalAssignmentEmailSubjectTemplate,
          approvalAssignmentEmailBodyTemplate,
          approvalDecisionAcceptEmailSubjectTemplate,
          approvalDecisionAcceptEmailBodyTemplate,
          approvalDecisionRejectEmailSubjectTemplate,
          approvalDecisionRejectEmailBodyTemplate,
          implAssignmentEmailSubjectTemplate,
          implAssignmentEmailBodyTemplate,
          implOwnerAcceptEmailSubjectTemplate,
          implOwnerAcceptEmailBodyTemplate,
          verificationAssignmentEmailSubjectTemplate,
          verificationAssignmentEmailBodyTemplate,
          verificationOutcomeEffectiveEmailSubjectTemplate,
          verificationOutcomeEffectiveEmailBodyTemplate,
          verificationOutcomeNotEffectiveEmailSubjectTemplate,
          verificationOutcomeNotEffectiveEmailBodyTemplate,
        },
        userData,
      );
      showAlert({
        title: "Success",
        message: `${scopeLabel} saved successfully.`,
        type: "success",
      });
      await loadConfig({ showBlockingLoader: false, useGlobalLoader: false });
    } catch (e) {
      showAlert({
        title: "Error",
        message: e?.message || "Failed to save CAPA config.",
        type: "danger",
      });
    } finally {
      hideLoader();
      setSaving(false);
    }
  };

  const handleSave = (scopeLabel = "CAPA configuration") => {
    const warningNote = numberingTokenWarnings.length
      ? `\n\nWarnings:\n- ${numberingTokenWarnings.join("\n- ")}`
      : "";
    showConfirmModal({
      title: "Save CAPA Config",
      message:
        "This update will affect dropdown values and defaults for this organization." +
        warningNote,
      confirmText: "Save",
      cancelText: "Cancel",
      confirmButtonClass: "btn-primary",
      icon: "fas fa-save text-primary",
      onConfirm: () => executeSave(scopeLabel),
    });
  };

  if (!userData) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border" role="status" />
      </div>
    );
  }
  if (!canConfigureCapa) return <Navigate to="/notification-center" replace />;

  return (
    <div className="user-mgmt-page capa-config-saas">
      <PageBreadcrumb
        items={[{ label: "Home", path: "/notification-center" }, { label: "CAPA Config" }]}
        actions={[
          {
            icon: "fas fa-refresh",
            label: reloading ? "Refreshing..." : "Refresh",
            onClick: () => loadConfig({ showBlockingLoader: false }),
          },
        ]}
      />

      {loading ? (
        <div className="card">
          <div className="card-body text-muted">Loading CAPA configuration...</div>
        </div>
      ) : (
        <div className="row g-3 capa-config-saas__layout">
          <div className="col-md-4 col-xl-3 capa-config-saas__nav-col">
            <div className="card capa-config-saas__nav-card">
              <div className="card-header fw-semibold">Configuration sections</div>
              <div className="list-group list-group-flush capa-config-saas__nav-list">
                {CONFIG_SECTIONS.map((section) => (
                  <button
                    key={section.key}
                    type="button"
                    className={`list-group-item list-group-item-action capa-config-saas__nav-item ${
                      activeSection === section.key ? "active" : ""
                    }`}
                    onClick={() => {
                      if (hasUnsavedChanges) {
                        showConfirmModal({
                          title: "Unsaved Changes",
                          message: "You have unsaved changes. Switch section without saving?",
                          confirmText: "Switch",
                          cancelText: "Stay",
                          confirmButtonClass: "btn-warning",
                          icon: "fas fa-exclamation-triangle text-warning",
                          onConfirm: () => setActiveSection(section.key),
                        });
                        return;
                      }
                      setActiveSection(section.key);
                    }}
                  >
                    <div className="fw-semibold capa-config-saas__nav-item-title">{section.label}</div>
                    <div className={`small capa-config-saas__nav-item-subtitle ${activeSection === section.key ? "text-white-50" : "text-muted"}`}>
                      {section.subtitle}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="col-md-8 col-xl-9 capa-config-saas__content-col">
            {activeSection === "numbering" ? (
              <div className="card shadow-sm capa-config-saas__panel-card">
                <div className="card-body">
                  {hasUnsavedChanges ? (
                    <div className="alert alert-warning py-2 px-3 mb-3">
                      <i className="fas fa-exclamation-triangle me-2" />
                      You have unsaved changes. Click section <strong>Save</strong> to apply updates.
                    </div>
                  ) : null}
                  <h6 className="mb-1">Numbering & SLA</h6>
                  <p className="text-muted small mb-3">
                    Configure CAPA identifier format and default SLA. These settings
                    apply at organization level.
                  </p>
                  <div className="row g-3">
                    <div className="col-md-8">
                      <label className="form-label">Numbering format</label>
                      <input
                        className="form-control"
                        value={numberingFormat}
                        onChange={(e) => setNumberingFormat(e.target.value)}
                        placeholder="CAPA-{YYYY}-{SEQ}"
                      />
                      <div className="small text-muted mt-2">
                        Supported tokens:{" "}
                        <code>{"{YYYY}"}</code>, <code>{"{YY}"}</code>,{" "}
                        <code>{"{MM}"}</code>, <code>{"{SEQ}"}</code>
                      </div>
                      <div className="small text-muted mt-1">
                        Preview:{" "}
                        <strong>
                          {String(numberingFormat || "CAPA-{YYYY}-{SEQ}")
                            .replace("{YYYY}", "2026")
                            .replace("{YY}", "26")
                            .replace("{MM}", "04")
                            .replace("{SEQ}", "0001")}
                        </strong>
                      </div>
                      {numberingTokenWarnings.length > 0 ? (
                        <div className="alert alert-warning mt-2 py-2 px-3 mb-0">
                          <ul className="mb-0 ps-3 small">
                            {numberingTokenWarnings.map((w) => (
                              <li key={w}>{w}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Default SLA (days)</label>
                      <input
                        type="number"
                        min="1"
                        className="form-control"
                        value={defaultSlaDays}
                        onChange={(e) => setDefaultSlaDays(e.target.value)}
                      />
                      <div className="small text-muted mt-2">
                        SLA means target completion timeline. Example: 30 days means CAPA
                        should be closed in 30 days from identification.
                      </div>
                    </div>
                  </div>
                  <div className="d-flex justify-content-end mt-3">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => handleSave("Numbering & SLA settings")}
                      disabled={saving || reloading}
                    >
                      {saving || reloading
                        ? saving
                          ? "Saving..."
                          : "Loading..."
                        : "Save Numbering & SLA"}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {activeSection === "sourceTypes" ? (
              <MasterListEditor
                title="Source Types"
                description="Manage Source Type dropdown values used in CAPA create/edit."
                items={sourceTypes}
                setItems={setSourceTypes}
                usedValues={sourceTypeUsage}
                onSave={() => handleSave("Source Types")}
                saving={saving}
                reloading={reloading}
                saveLabel="Save Source Types"
                onNotify={showAlert}
                showUnsavedNotice={hasUnsavedChanges}
              />
            ) : null}

            {activeSection === "rcaMethods" ? (
              <MasterListEditor
                title="RCA Methods"
                description="Manage RCA Method dropdown values used in CAPA RCA stage."
                items={rcaMethods}
                setItems={setRcaMethods}
                usedValues={rcaMethodUsage}
                onSave={() => handleSave("RCA Methods")}
                saving={saving}
                reloading={reloading}
                saveLabel="Save RCA Methods"
                onNotify={showAlert}
                showUnsavedNotice={hasUnsavedChanges}
              />
            ) : null}

            {activeSection === "statuses" ? (
              <MasterListEditor
                title="CAPA Statuses"
                description="CAPA workflow status labels are managed by backend and are read-only here."
                items={statuses}
                setItems={setStatuses}
                usedValues={statusUsage}
                readOnly={true}
                onSave={() => handleSave("CAPA Statuses")}
                saving={saving}
                reloading={reloading}
                saveLabel="Save CAPA Statuses"
                onNotify={showAlert}
                showUnsavedNotice={hasUnsavedChanges}
              />
            ) : null}

            {activeSection === "actionPlanningEmail" ? (
              <div className="card shadow-sm capa-config-saas__panel-card capa-ap-email-panel-card">
                <div className="card-body capa-ap-email-panel-body">
                  {hasUnsavedChanges ? (
                    <div className="alert alert-warning py-2 px-3 mb-3 flex-shrink-0">
                      <i className="fas fa-exclamation-triangle me-2" />
                      You have unsaved changes. Click <strong>Save</strong> to apply updates.
                    </div>
                  ) : null}
                  <h6 className="mb-2 flex-shrink-0">Action planning email (assignment)</h6>
                  <div className="capa-ap-email-editor-grow">
                    <CapaActionPlanningEmailConfigEditor
                      subjectTemplate={actionPlanningEmailSubjectTemplate}
                      onSubjectChange={setActionPlanningEmailSubjectTemplate}
                      bodyTemplate={actionPlanningEmailBodyTemplate}
                      onBodyChange={setActionPlanningEmailBodyTemplate}
                      onTagCopied={(tag) =>
                        showAlert({
                          title: "Copied",
                          message: `${tag} copied to clipboard.`,
                          type: "success",
                        })
                      }
                      description="Per-organization templates for notifying staff when an action-planning task is assigned. Defaults are stored in the database. Click a tag to copy it."
                      fillAvailableHeight
                    />
                  </div>
                  <div className="capa-ap-email-panel-footer d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => handleSave("Action planning email templates")}
                      disabled={saving || reloading}
                    >
                      {saving || reloading
                        ? saving
                          ? "Saving..."
                          : "Loading..."
                        : "Save email templates"}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {activeSection === "actionPlanningTaskAcceptEmail" ? (
              <div className="card shadow-sm capa-config-saas__panel-card capa-ap-email-panel-card">
                <div className="card-body capa-ap-email-panel-body">
                  {hasUnsavedChanges ? (
                    <div className="alert alert-warning py-2 px-3 mb-3 flex-shrink-0">
                      <i className="fas fa-exclamation-triangle me-2" />
                      You have unsaved changes. Click <strong>Save</strong> to apply updates.
                    </div>
                  ) : null}
                  <h6 className="mb-2 flex-shrink-0">Action planning — task accepted (notify CAPA owner)</h6>
                  <div className="capa-ap-email-editor-grow">
                    <CapaActionPlanningEmailConfigEditor
                      subjectTemplate={actionPlanningTaskAcceptEmailSubjectTemplate}
                      onSubjectChange={setActionPlanningTaskAcceptEmailSubjectTemplate}
                      bodyTemplate={actionPlanningTaskAcceptEmailBodyTemplate}
                      onBodyChange={setActionPlanningTaskAcceptEmailBodyTemplate}
                      mergeTags={CAPA_ACTION_PLANNING_DECISION_EMAIL_MERGE_TAGS}
                      onTagCopied={(tag) =>
                        showAlert({
                          title: "Copied",
                          message: `${tag} copied to clipboard.`,
                          type: "success",
                        })
                      }
                      description="Per-organization templates emailed to the CAPA owner when an assignee accepts a task (CAPAConfig ActionPlanningTaskAccept*). Includes assignment merge tags plus Decision, DecisionReason, DecidedByName, and TaskAssigneeName. Click a tag to copy it."
                      fillAvailableHeight
                    />
                  </div>
                  <div className="capa-ap-email-panel-footer d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => handleSave("Action planning task accepted email templates")}
                      disabled={saving || reloading}
                    >
                      {saving || reloading
                        ? saving
                          ? "Saving..."
                          : "Loading..."
                        : "Save email templates"}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {activeSection === "actionPlanningTaskRejectEmail" ? (
              <div className="card shadow-sm capa-config-saas__panel-card capa-ap-email-panel-card">
                <div className="card-body capa-ap-email-panel-body">
                  {hasUnsavedChanges ? (
                    <div className="alert alert-warning py-2 px-3 mb-3 flex-shrink-0">
                      <i className="fas fa-exclamation-triangle me-2" />
                      You have unsaved changes. Click <strong>Save</strong> to apply updates.
                    </div>
                  ) : null}
                  <h6 className="mb-2 flex-shrink-0">Action planning — task rejected (notify CAPA owner)</h6>
                  <div className="capa-ap-email-editor-grow">
                    <CapaActionPlanningEmailConfigEditor
                      subjectTemplate={actionPlanningTaskRejectEmailSubjectTemplate}
                      onSubjectChange={setActionPlanningTaskRejectEmailSubjectTemplate}
                      bodyTemplate={actionPlanningTaskRejectEmailBodyTemplate}
                      onBodyChange={setActionPlanningTaskRejectEmailBodyTemplate}
                      mergeTags={CAPA_ACTION_PLANNING_DECISION_EMAIL_MERGE_TAGS}
                      onTagCopied={(tag) =>
                        showAlert({
                          title: "Copied",
                          message: `${tag} copied to clipboard.`,
                          type: "success",
                        })
                      }
                      description="Per-organization templates emailed to the CAPA owner when an assignee rejects a task (CAPAConfig ActionPlanningTaskReject*). Same merge tags as the accepted template. Click a tag to copy it."
                      fillAvailableHeight
                    />
                  </div>
                  <div className="capa-ap-email-panel-footer d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => handleSave("Action planning task rejected email templates")}
                      disabled={saving || reloading}
                    >
                      {saving || reloading
                        ? saving
                          ? "Saving..."
                          : "Loading..."
                        : "Save email templates"}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {activeSection === "approvalAssignmentEmail" ? (
              <div className="card shadow-sm capa-config-saas__panel-card capa-ap-email-panel-card">
                <div className="card-body capa-ap-email-panel-body">
                  {hasUnsavedChanges ? (
                    <div className="alert alert-warning py-2 px-3 mb-3 flex-shrink-0">
                      <i className="fas fa-exclamation-triangle me-2" />
                      You have unsaved changes. Click <strong>Save</strong> to apply updates.
                    </div>
                  ) : null}
                  <h6 className="mb-2 flex-shrink-0">Approval — assignment (notify approver)</h6>
                  <div className="capa-ap-email-editor-grow">
                    <CapaActionPlanningEmailConfigEditor
                      subjectTemplate={approvalAssignmentEmailSubjectTemplate}
                      onSubjectChange={setApprovalAssignmentEmailSubjectTemplate}
                      bodyTemplate={approvalAssignmentEmailBodyTemplate}
                      onBodyChange={setApprovalAssignmentEmailBodyTemplate}
                      mergeTags={CAPA_APPROVAL_ASSIGNMENT_EMAIL_MERGE_TAGS}
                      onTagCopied={(tag) =>
                        showAlert({
                          title: "Copied",
                          message: `${tag} copied to clipboard.`,
                          type: "success",
                        })
                      }
                      description="Per-organization templates when a staff member is assigned as a CAPA approver (CAPAConfig ApprovalAssignment*). Click a tag to copy it."
                      fillAvailableHeight
                    />
                  </div>
                  <div className="capa-ap-email-panel-footer d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => handleSave("CAPA approval assignment email templates")}
                      disabled={saving || reloading}
                    >
                      {saving || reloading
                        ? saving
                          ? "Saving..."
                          : "Loading..."
                        : "Save email templates"}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {activeSection === "approvalDecisionAcceptEmail" ? (
              <div className="card shadow-sm capa-config-saas__panel-card capa-ap-email-panel-card">
                <div className="card-body capa-ap-email-panel-body">
                  {hasUnsavedChanges ? (
                    <div className="alert alert-warning py-2 px-3 mb-3 flex-shrink-0">
                      <i className="fas fa-exclamation-triangle me-2" />
                      You have unsaved changes. Click <strong>Save</strong> to apply updates.
                    </div>
                  ) : null}
                  <h6 className="mb-2 flex-shrink-0">Approval — accepted (notify CAPA owner)</h6>
                  <div className="capa-ap-email-editor-grow">
                    <CapaActionPlanningEmailConfigEditor
                      subjectTemplate={approvalDecisionAcceptEmailSubjectTemplate}
                      onSubjectChange={setApprovalDecisionAcceptEmailSubjectTemplate}
                      bodyTemplate={approvalDecisionAcceptEmailBodyTemplate}
                      onBodyChange={setApprovalDecisionAcceptEmailBodyTemplate}
                      mergeTags={CAPA_APPROVAL_DECISION_EMAIL_MERGE_TAGS}
                      onTagCopied={(tag) =>
                        showAlert({
                          title: "Copied",
                          message: `${tag} copied to clipboard.`,
                          type: "success",
                        })
                      }
                      description="Per-organization templates emailed to the CAPA owner when an approver accepts (CAPAConfig ApprovalDecisionAccept*). Click a tag to copy it."
                      fillAvailableHeight
                    />
                  </div>
                  <div className="capa-ap-email-panel-footer d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => handleSave("CAPA approval accepted email templates")}
                      disabled={saving || reloading}
                    >
                      {saving || reloading
                        ? saving
                          ? "Saving..."
                          : "Loading..."
                        : "Save email templates"}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {activeSection === "approvalDecisionRejectEmail" ? (
              <div className="card shadow-sm capa-config-saas__panel-card capa-ap-email-panel-card">
                <div className="card-body capa-ap-email-panel-body">
                  {hasUnsavedChanges ? (
                    <div className="alert alert-warning py-2 px-3 mb-3 flex-shrink-0">
                      <i className="fas fa-exclamation-triangle me-2" />
                      You have unsaved changes. Click <strong>Save</strong> to apply updates.
                    </div>
                  ) : null}
                  <h6 className="mb-2 flex-shrink-0">Approval — rejected (notify CAPA owner)</h6>
                  <div className="capa-ap-email-editor-grow">
                    <CapaActionPlanningEmailConfigEditor
                      subjectTemplate={approvalDecisionRejectEmailSubjectTemplate}
                      onSubjectChange={setApprovalDecisionRejectEmailSubjectTemplate}
                      bodyTemplate={approvalDecisionRejectEmailBodyTemplate}
                      onBodyChange={setApprovalDecisionRejectEmailBodyTemplate}
                      mergeTags={CAPA_APPROVAL_DECISION_EMAIL_MERGE_TAGS}
                      onTagCopied={(tag) =>
                        showAlert({
                          title: "Copied",
                          message: `${tag} copied to clipboard.`,
                          type: "success",
                        })
                      }
                      description="Per-organization templates emailed to the CAPA owner when an approver rejects (CAPAConfig ApprovalDecisionReject*). Click a tag to copy it."
                      fillAvailableHeight
                    />
                  </div>
                  <div className="capa-ap-email-panel-footer d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => handleSave("CAPA approval rejected email templates")}
                      disabled={saving || reloading}
                    >
                      {saving || reloading
                        ? saving
                          ? "Saving..."
                          : "Loading..."
                        : "Save email templates"}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {activeSection === "implementationAssignmentEmail" ? (
              <div className="card shadow-sm capa-config-saas__panel-card capa-ap-email-panel-card">
                <div className="card-body capa-ap-email-panel-body">
                  {hasUnsavedChanges ? (
                    <div className="alert alert-warning py-2 px-3 mb-3 flex-shrink-0">
                      <i className="fas fa-exclamation-triangle me-2" />
                      You have unsaved changes. Click <strong>Save</strong> to apply updates.
                    </div>
                  ) : null}
                  <h6 className="mb-2 flex-shrink-0">Implementation — task assignment</h6>
                  <p className="text-muted small mb-3">
                    Emailed to the staff member when an implementation task is created (CAPAConfig
                    ImplAssignment*).
                  </p>
                  <div className="capa-ap-email-editor-grow">
                    <CapaActionPlanningEmailConfigEditor
                      subjectTemplate={implAssignmentEmailSubjectTemplate}
                      onSubjectChange={setImplAssignmentEmailSubjectTemplate}
                      bodyTemplate={implAssignmentEmailBodyTemplate}
                      onBodyChange={setImplAssignmentEmailBodyTemplate}
                      mergeTags={CAPA_IMPLEMENTATION_ASSIGNMENT_EMAIL_MERGE_TAGS}
                      onTagCopied={(tag) =>
                        showAlert({
                          title: "Copied",
                          message: `${tag} copied to clipboard.`,
                          type: "success",
                        })
                      }
                      description="Merge tags match the assignment payload. Click a tag to copy it."
                      fillAvailableHeight
                    />
                  </div>
                  <div className="capa-ap-email-panel-footer d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => handleSave("Implementation task assignment email templates")}
                      disabled={saving || reloading}
                    >
                      {saving || reloading
                        ? saving
                          ? "Saving..."
                          : "Loading..."
                        : "Save email templates"}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {activeSection === "implementationStatusChangeEmail" ? (
              <div className="card shadow-sm capa-config-saas__panel-card capa-ap-email-panel-card">
                <div className="card-body capa-ap-email-panel-body">
                  {hasUnsavedChanges ? (
                    <div className="alert alert-warning py-2 px-3 mb-3 flex-shrink-0">
                      <i className="fas fa-exclamation-triangle me-2" />
                      You have unsaved changes. Click <strong>Save</strong> to apply updates.
                    </div>
                  ) : null}
                  <h6 className="mb-2 flex-shrink-0">Implementation — status change (notify CAPA owner)</h6>
                  <p className="text-muted small mb-3">
                    One template for every implementation status change (including Deferred). Merge
                    tokens <strong>PreviousStatus</strong> and <strong>NewStatus</strong> show the old
                    and new values; <strong>Decision</strong> repeats the new status. Stored as
                    ImplOwnerAccept* in CAPAConfig; legacy ImplOwnerReject* columns are kept in sync
                    when you save.
                  </p>

                  <div className="flex-shrink-0 capa-impl-status-editor-block">
                    <CapaActionPlanningEmailConfigEditor
                      subjectTemplate={implOwnerAcceptEmailSubjectTemplate}
                      onSubjectChange={setImplOwnerAcceptEmailSubjectTemplate}
                      bodyTemplate={implOwnerAcceptEmailBodyTemplate}
                      onBodyChange={setImplOwnerAcceptEmailBodyTemplate}
                      mergeTags={CAPA_IMPLEMENTATION_OWNER_DECISION_EMAIL_MERGE_TAGS}
                      onTagCopied={(tag) =>
                        showAlert({
                          title: "Copied",
                          message: `${tag} copied to clipboard.`,
                          type: "success",
                        })
                      }
                      description="Use {{PreviousStatus}}, {{NewStatus}}, {{DecisionReason}}, {{DecidedByName}}, {{TaskAssigneeName}}, and {{DueDate}}. Click a tag to copy it."
                      fillAvailableHeight={false}
                    />
                  </div>

                  <div className="capa-ap-email-panel-footer d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() =>
                        handleSave("Implementation status change (owner) email templates")
                      }
                      disabled={saving || reloading}
                    >
                      {saving || reloading
                        ? saving
                          ? "Saving..."
                          : "Loading..."
                        : "Save email templates"}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {activeSection === "verificationAssignmentEmail" ? (
              <div className="card shadow-sm capa-config-saas__panel-card capa-ap-email-panel-card">
                <div className="card-body capa-ap-email-panel-body">
                  {hasUnsavedChanges ? (
                    <div className="alert alert-warning py-2 px-3 mb-3 flex-shrink-0">
                      <i className="fas fa-exclamation-triangle me-2" />
                      You have unsaved changes. Click <strong>Save</strong> to apply updates.
                    </div>
                  ) : null}
                  <h6 className="mb-2 flex-shrink-0">Verification — assignment (notify verifier)</h6>
                  <p className="text-muted small mb-3">
                    Sent when a verification request is created in the Verification stage (
                    VerificationAssignmentEmail* in CAPAConfig).
                  </p>
                  <div className="capa-ap-email-editor-grow">
                    <CapaActionPlanningEmailConfigEditor
                      subjectTemplate={verificationAssignmentEmailSubjectTemplate}
                      onSubjectChange={setVerificationAssignmentEmailSubjectTemplate}
                      bodyTemplate={verificationAssignmentEmailBodyTemplate}
                      onBodyChange={setVerificationAssignmentEmailBodyTemplate}
                      mergeTags={CAPA_VERIFICATION_ASSIGNMENT_EMAIL_MERGE_TAGS}
                      onTagCopied={(tag) =>
                        showAlert({
                          title: "Copied",
                          message: `${tag} copied to clipboard.`,
                          type: "success",
                        })
                      }
                      description="{{OwnerName}} is the assigned verifier. {{Description}} holds verification instructions. Click a tag to copy it."
                      fillAvailableHeight
                    />
                  </div>
                  <div className="capa-ap-email-panel-footer d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => handleSave("Verification assignment email templates")}
                      disabled={saving || reloading}
                    >
                      {saving || reloading
                        ? saving
                          ? "Saving..."
                          : "Loading..."
                        : "Save email templates"}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {activeSection === "verificationOutcomeEffectiveEmail" ? (
              <div className="card shadow-sm capa-config-saas__panel-card capa-ap-email-panel-card">
                <div className="card-body capa-ap-email-panel-body">
                  {hasUnsavedChanges ? (
                    <div className="alert alert-warning py-2 px-3 mb-3 flex-shrink-0">
                      <i className="fas fa-exclamation-triangle me-2" />
                      You have unsaved changes. Click <strong>Save</strong> to apply updates.
                    </div>
                  ) : null}
                  <h6 className="mb-2 flex-shrink-0">Verification — effective (notify CAPA owner)</h6>
                  <p className="text-muted small mb-3">
                    Emailed to the CAPA owner when the assigned verifier submits an{" "}
                    <strong>Effective</strong> outcome (VerificationOutcomeEffectiveEmail*).
                  </p>
                  <div className="capa-ap-email-editor-grow">
                    <CapaActionPlanningEmailConfigEditor
                      subjectTemplate={verificationOutcomeEffectiveEmailSubjectTemplate}
                      onSubjectChange={setVerificationOutcomeEffectiveEmailSubjectTemplate}
                      bodyTemplate={verificationOutcomeEffectiveEmailBodyTemplate}
                      onBodyChange={setVerificationOutcomeEffectiveEmailBodyTemplate}
                      mergeTags={CAPA_VERIFICATION_OWNER_OUTCOME_EMAIL_MERGE_TAGS}
                      onTagCopied={(tag) =>
                        showAlert({
                          title: "Copied",
                          message: `${tag} copied to clipboard.`,
                          type: "success",
                        })
                      }
                      description="{{Decision}} will be Effective. {{DecidedByName}} is the verifier. Click a tag to copy it."
                      fillAvailableHeight
                    />
                  </div>
                  <div className="capa-ap-email-panel-footer d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => handleSave("Verification effective outcome email templates")}
                      disabled={saving || reloading}
                    >
                      {saving || reloading
                        ? saving
                          ? "Saving..."
                          : "Loading..."
                        : "Save email templates"}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {activeSection === "verificationOutcomeNotEffectiveEmail" ? (
              <div className="card shadow-sm capa-config-saas__panel-card capa-ap-email-panel-card">
                <div className="card-body capa-ap-email-panel-body">
                  {hasUnsavedChanges ? (
                    <div className="alert alert-warning py-2 px-3 mb-3 flex-shrink-0">
                      <i className="fas fa-exclamation-triangle me-2" />
                      You have unsaved changes. Click <strong>Save</strong> to apply updates.
                    </div>
                  ) : null}
                  <h6 className="mb-2 flex-shrink-0">
                    Verification — not effective (notify CAPA owner)
                  </h6>
                  <p className="text-muted small mb-3">
                    Emailed to the CAPA owner when the assigned verifier submits a{" "}
                    <strong>Not Effective</strong> outcome (VerificationOutcomeNotEffectiveEmail*).
                  </p>
                  <div className="capa-ap-email-editor-grow">
                    <CapaActionPlanningEmailConfigEditor
                      subjectTemplate={verificationOutcomeNotEffectiveEmailSubjectTemplate}
                      onSubjectChange={setVerificationOutcomeNotEffectiveEmailSubjectTemplate}
                      bodyTemplate={verificationOutcomeNotEffectiveEmailBodyTemplate}
                      onBodyChange={setVerificationOutcomeNotEffectiveEmailBodyTemplate}
                      mergeTags={CAPA_VERIFICATION_OWNER_OUTCOME_EMAIL_MERGE_TAGS}
                      onTagCopied={(tag) =>
                        showAlert({
                          title: "Copied",
                          message: `${tag} copied to clipboard.`,
                          type: "success",
                        })
                      }
                      description="{{Decision}} will be Not Effective. {{DecidedByName}} is the verifier. Click a tag to copy it."
                      fillAvailableHeight
                    />
                  </div>
                  <div className="capa-ap-email-panel-footer d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => handleSave("Verification not effective outcome email templates")}
                      disabled={saving || reloading}
                    >
                      {saving || reloading
                        ? saving
                          ? "Saving..."
                          : "Loading..."
                        : "Save email templates"}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      <InfoAlert isOpen={alertState.isOpen} onClose={hideAlert} {...alertState} />
      <ConfirmModal
        isOpen={confirmState.isOpen}
        onClose={closeConfirmModal}
        onConfirm={() => {
          const fn = confirmState.onConfirm;
          closeConfirmModal();
          fn?.();
        }}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        confirmButtonClass={confirmState.confirmButtonClass}
        icon={confirmState.icon}
      />
    </div>
  );
};

export default CapaConfigMaster;

